import React, { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ArrowForward,
  SwapHoriz,
  LockOutlined,
  LogoutRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  getAppLogo,
  CLIENT_THEME,
  clientThemePalette,
} from 'utils/clientTheme';
import type { ThemeModePalette } from 'utils/clientTheme';
import { SVG } from 'components/Asset/SVG';
import { updateSelectedModuleState } from 'store/app.slice';
import { useTranslation } from 'react-i18next';
import xMachinesIcon from 'assets/icons/Xmachines.png';
import xPartsIcon from 'assets/icons/Xparts.png';
import xTiresIcon from 'assets/icons/Xtire.png';

type AppModule = 'machines' | 'parts' | 'tires';

interface ModuleCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  isActive: boolean;
  palette: ThemeModePalette;
  isYokohamaClient: boolean;
  module: AppModule;
  index: number;
}

const ModuleIconImage = ({ src, alt }: { src: string; alt: string }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: 34,
      height: 34,
      objectFit: 'contain',
      display: 'block',
    }}
  />
);

const MachinesIcon = () => (
  <ModuleIconImage src={xMachinesIcon} alt="X.Machines" />
);

const PartsIcon = () => <ModuleIconImage src={xPartsIcon} alt="X.Parts" />;

const TiresIcon = () => <ModuleIconImage src={xTiresIcon} alt="X.Tires" />;

const MachinesLineIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="14" width="16" height="10" rx="1.5" />
    <path d="M19 18h3a2 2 0 002-2v-4a2 2 0 00-2-2h-3" />
    <path d="M7 14V10a4 4 0 014-4h2" />
    <circle cx="17" cy="10" r="2.5" />
    <circle cx="7" cy="21" r="1.5" />
    <circle cx="15" cy="21" r="1.5" />
  </svg>
);

const PartsLineIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4.5a1 1 0 000 1.4l1.8 1.8a1 1 0 001.4 0l3.2-3.2a6.5 6.5 0 01-8 8L7 19.8a2.3 2.3 0 01-3.2-3.2L11 9a6.5 6.5 0 018-8l-3 3.5z" />
    <circle cx="7" cy="21" r="1.2" />
  </svg>
);

const TiresLineIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="14" cy="14" r="10.5" />
    <circle cx="14" cy="14" r="4" />
    <path d="M14 3.5v3M14 21.5v3M3.5 14h3M21.5 14h3" />
    <path d="M6.4 6.4l2.1 2.1M19.5 19.5l2.1 2.1M19.5 6.4l-2.1 2.1M6.4 19.5l2.1 2.1" />
  </svg>
);

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  icon,
  path,
  isActive,
  palette,
  isYokohamaClient,
  module,
  index,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);

  // A card is "disabled" when the module is not enabled for this customer
  const disabled = !isActive;

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 100 + index * 120);
    return () => {
      clearTimeout(timer);
    };
  }, [index]);

  const handleClick = () => {
    if (disabled) return;
    dispatch(updateSelectedModuleState(module));
    navigate(path);
  };

  const accent = isYokohamaClient ? palette.primary : '#5e85f0';
  const accentHoverText = isYokohamaClient ? palette.primary : '#7ea3f5';
  const accentSoft = isYokohamaClient
    ? `${palette.primary}26`
    : 'rgba(94,133,240,0.08)';
  const accentHover = isYokohamaClient
    ? `${palette.primary}3d`
    : 'rgba(94,133,240,0.2)';
  const iconAccentSoft = isYokohamaClient ? accentSoft : 'rgba(94,133,240,0.1)';
  const iconAccentHover = isYokohamaClient
    ? `linear-gradient(135deg, ${accentHover} 0%, ${accentSoft} 100%)`
    : 'linear-gradient(135deg, rgba(94,133,240,0.2) 0%, rgba(94,133,240,0.1) 100%)';
  const surface = isYokohamaClient
    ? 'linear-gradient(145deg, rgba(26,26,26,0.94) 0%, rgba(13,13,13,0.98) 100%)'
    : 'linear-gradient(145deg, rgba(22,28,48,0.9) 0%, rgba(16,20,38,0.95) 100%)';
  const surfaceHover = isYokohamaClient
    ? 'linear-gradient(145deg, rgba(37,31,32,0.98) 0%, rgba(22,22,22,1) 100%)'
    : 'linear-gradient(145deg, rgba(30,38,60,0.95) 0%, rgba(22,28,48,0.98) 100%)';
  const disabledSurface = isYokohamaClient
    ? 'linear-gradient(145deg, rgba(18,18,18,0.72) 0%, rgba(12,12,12,0.8) 100%)'
    : 'linear-gradient(145deg, rgba(14,18,32,0.7) 0%, rgba(10,13,24,0.75) 100%)';
  const primaryText = isYokohamaClient ? palette.textPrimary : '#e8eaf6';
  const secondaryText = isYokohamaClient
    ? 'rgba(218,224,235,0.68)'
    : 'rgba(180,190,220,0.65)';

  return (
    <Box
      onMouseEnter={() => {
        if (!disabled) setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: disabled
          ? isYokohamaClient
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(255,255,255,0.05)'
          : isActive
          ? isYokohamaClient
            ? `${accent}99`
            : 'rgba(94,133,240,0.6)'
          : hovered
          ? isYokohamaClient
            ? `${accent}4d`
            : 'rgba(94,133,240,0.3)'
          : isYokohamaClient
          ? palette.cardBorderColor
          : 'rgba(255,255,255,0.07)',
        background: disabled
          ? disabledSurface
          : hovered
          ? surfaceHover
          : surface,
        p: '28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        position: 'relative',
        overflow: 'hidden',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: mounted ? (disabled ? 0.5 : 1) : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        backdropFilter: 'blur(12px)',
        // Desaturate the whole card when disabled
        filter: disabled ? 'grayscale(0.6)' : 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: disabled
            ? isYokohamaClient
              ? 'linear-gradient(90deg, transparent, rgba(120,120,120,0.16), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)'
            : isActive
            ? isYokohamaClient
              ? `linear-gradient(90deg, transparent, ${accent}, transparent)`
              : 'linear-gradient(90deg, transparent, rgba(94,133,240,0.8), transparent)'
            : isYokohamaClient
            ? 'linear-gradient(90deg, transparent, rgba(150,150,150,0.18), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          opacity: hovered ? 1 : 0.6,
          transition: 'opacity 0.3s ease',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: isYokohamaClient ? undefined : 0,
          right: isYokohamaClient ? undefined : 0,
          width: isYokohamaClient ? undefined : '120px',
          height: isYokohamaClient ? undefined : '120px',
          inset: isYokohamaClient ? 0 : undefined,
          borderRadius: isYokohamaClient ? undefined : '50%',
          background: isYokohamaClient
            ? `linear-gradient(135deg, transparent 58%, ${accentSoft} 100%)`
            : 'radial-gradient(circle, rgba(94,133,240,0.06) 0%, transparent 70%)',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
        },
      }}
      onClick={handleClick}
    >
      {/* Status badge — "Active" or "Not enabled" */}
      <Chip
        label={disabled ? t('module_not_enabled') : t('module_active')}
        size="small"
        icon={
          disabled ? (
            <LockOutlined
              sx={{
                fontSize: '0.65rem !important',
                color: 'rgba(140,150,180,0.6) !important',
              }}
            />
          ) : undefined
        }
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: disabled
            ? 'rgba(255,255,255,0.04)'
            : isYokohamaClient
            ? `linear-gradient(135deg, ${accentHover}, ${accentSoft})`
            : 'linear-gradient(135deg, rgba(94,133,240,0.25), rgba(94,133,240,0.15))',
          border: '1px solid',
          borderColor: disabled
            ? 'rgba(255,255,255,0.08)'
            : isYokohamaClient
            ? `${accent}66`
            : 'rgba(94,133,240,0.4)',
          color: disabled
            ? 'rgba(140,150,180,0.55)'
            : isYokohamaClient
            ? accent
            : '#7ea3f5',
          fontWeight: 600,
          fontSize: '0.6rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          height: 22,
          backdropFilter: 'blur(8px)',
          // Always show the badge (removed the conditional render)
          display: 'flex',
        }}
      />

      {/* Icon container */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '14px',
          background: disabled
            ? 'rgba(255,255,255,0.03)'
            : hovered
            ? iconAccentHover
            : iconAccentSoft,
          border: '1px solid',
          borderColor: disabled
            ? 'rgba(255,255,255,0.06)'
            : hovered
            ? isYokohamaClient
              ? `${accent}59`
              : 'rgba(94,133,240,0.35)'
            : isYokohamaClient
            ? `${accent}26`
            : 'rgba(94,133,240,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: disabled
            ? 'rgba(120,130,160,0.4)'
            : hovered
            ? accentHoverText
            : accent,
          transition: 'all 0.3s ease',
          transform: hovered ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.15rem',
            mb: '8px',
            color: disabled
              ? isYokohamaClient
                ? 'rgba(140,150,160,0.48)'
                : 'rgba(160,170,200,0.45)'
              : primaryText,
            letterSpacing: isYokohamaClient ? 0 : '-0.01em',
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: disabled ? 'rgba(140,150,180,0.3)' : secondaryText,
            fontSize: '0.83rem',
            lineHeight: 1.65,
            fontWeight: 400,
          }}
        >
          {description}
        </Typography>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderTopColor: disabled
            ? 'rgba(255,255,255,0.04)'
            : isYokohamaClient
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(255,255,255,0.06)',
          pt: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 600,
            color: disabled
              ? 'rgba(120,130,160,0.35)'
              : hovered
              ? accentHoverText
              : accent,
            letterSpacing: '0.02em',
            transition: 'color 0.2s ease',
          }}
        >
          {disabled ? t('contact_sales_to_enable') : t('view_module')}
        </Typography>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            background: disabled
              ? 'rgba(255,255,255,0.03)'
              : hovered
              ? accentHover
              : accentSoft,
            border: '1px solid',
            borderColor: disabled
              ? 'rgba(255,255,255,0.06)'
              : hovered
              ? isYokohamaClient
                ? `${accent}66`
                : 'rgba(94,133,240,0.4)'
              : isYokohamaClient
              ? `${accent}26`
              : 'rgba(94,133,240,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.25s ease',
            transform: hovered ? 'translateX(3px)' : 'translateX(0)',
          }}
        >
          {disabled ? (
            <LockOutlined
              sx={{
                fontSize: '0.8rem',
                color: 'rgba(120,130,160,0.35)',
              }}
            />
          ) : (
            <ArrowForward
              sx={{
                fontSize: '0.85rem',
                color: hovered ? accentHoverText : accent,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const HomeView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modules = useAppSelector((state) => state.authReducer.customer.modules);
  const role = useAppSelector((state) => state.authReducer.role);
  const customerName = useAppSelector(
    (state) => state.authReducer.customer.name
  );
  const logo = getAppLogo();
  const canSwitchCustomer = role === 'director' || role === 'internal-user';
  const isYokohamaClient = CLIENT_THEME === 'yokohama';
  const palette = clientThemePalette[CLIENT_THEME].dark;
  const pageBackground = isYokohamaClient
    ? 'linear-gradient(160deg, #0d0d0d 0%, #13090a 48%, #09090b 100%)'
    : 'linear-gradient(160deg, #0d1120 0%, #0a0e1a 40%, #080c18 100%)';
  const controlBackground = isYokohamaClient
    ? 'rgba(255,255,255,0.05)'
    : 'rgba(255,255,255,0.04)';
  const controlBorder = isYokohamaClient
    ? 'rgba(255,255,255,0.09)'
    : 'rgba(255,255,255,0.08)';
  const mutedText = 'rgba(218,224,235,0.68)';
  const customerTextColor = isYokohamaClient
    ? mutedText
    : 'rgba(200,210,240,0.75)';
  const controlIconColor = isYokohamaClient
    ? mutedText
    : 'rgba(150,165,210,0.6)';
  const controlHoverColor = isYokohamaClient ? palette.primary : '#7ea3f5';
  const controlHoverBackground = isYokohamaClient
    ? `${palette.primary}1f`
    : 'rgba(94,133,240,0.12)';
  const dividerBackground = isYokohamaClient
    ? `linear-gradient(90deg, transparent, ${palette.primary}, transparent)`
    : 'linear-gradient(90deg, transparent, rgba(94,133,240,0.5), transparent)';
  const taglineColor = isYokohamaClient ? mutedText : 'rgba(180,190,220,0.55)';
  const gridBackgroundImage = isYokohamaClient
    ? `
              linear-gradient(${palette.primary}0d 1px, transparent 1px),
              linear-gradient(90deg, ${palette.primary}0d 1px, transparent 1px)
            `
    : `
              linear-gradient(rgba(94,133,240,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(94,133,240,0.03) 1px, transparent 1px)
            `;
  const logoSize = isYokohamaClient
    ? { imgHeight: 82, svgWidth: 320, svgHeight: 100 }
    : { imgHeight: 100, svgWidth: 320, svgHeight: 100 };

  const [headerVisible, setHeaderVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setHeaderVisible(true);
    }, 50);
    return () => {
      clearTimeout(t);
    };
  }, []);

  // All 3 modules are always shown; isActive drives enabled/greyed state
  const moduleCards = [
    {
      title: 'X.Machines',
      description: t('x_machines_description'),
      icon: isYokohamaClient ? <MachinesLineIcon /> : <MachinesIcon />,
      path: '/fleet/summary',
      isActive: modules?.isMachinesModule ?? false,
      module: 'machines' as AppModule,
    },
    {
      title: 'X.Parts',
      description: t('x_parts_description'),
      icon: isYokohamaClient ? <PartsLineIcon /> : <PartsIcon />,
      path: '/x-parts/dashboard',
      isActive: modules?.isPartsModule ?? false,
      module: 'parts' as AppModule,
    },
    {
      title: 'X Tires',
      description: t('x_tires_description'),
      icon: isYokohamaClient ? <TiresLineIcon /> : <TiresIcon />,
      path: '/tire-insights/fleet-insight',
      isActive: modules?.isTiresModule ?? false,
      module: 'tires' as AppModule,
    },
  ];

  return (
    <CustomMuiThemeProvider>
      <Box
        sx={{
          minHeight: '100vh',
          background: pageBackground,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
          py: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: gridBackgroundImage,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: isYokohamaClient ? 0 : undefined,
            top: isYokohamaClient ? undefined : '-20%',
            left: isYokohamaClient ? undefined : '50%',
            transform: isYokohamaClient ? undefined : 'translateX(-50%)',
            width: isYokohamaClient ? undefined : '600px',
            height: isYokohamaClient ? undefined : '400px',
            borderRadius: isYokohamaClient ? undefined : '50%',
            background: isYokohamaClient
              ? 'linear-gradient(180deg, rgba(226,30,49,0.1) 0%, transparent 32%, rgba(226,30,49,0.06) 100%)'
              : 'radial-gradient(ellipse, rgba(94,133,240,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Top-right: switch customer + logout (separate boxes) */}
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 24,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {canSwitchCustomer && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: controlBackground,
                border: `1px solid ${controlBorder}`,
                borderRadius: '10px',
                px: '12px',
                py: '6px',
                backdropFilter: 'blur(12px)',
              }}
            >
              {customerName && (
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: customerTextColor,
                    fontWeight: 500,
                    letterSpacing: isYokohamaClient ? 0 : '0.01em',
                  }}
                >
                  {customerName}
                </Typography>
              )}
              <Tooltip title={t('switch_customer')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigate('/customer-selection');
                  }}
                  sx={{
                    color: controlIconColor,
                    width: 28,
                    height: 28,
                    '&:hover': {
                      color: controlHoverColor,
                      background: controlHoverBackground,
                    },
                  }}
                >
                  <SwapHoriz sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              background: controlBackground,
              border: `1px solid ${controlBorder}`,
              borderRadius: '10px',
              px: '8px',
              py: '6px',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Tooltip title={t('logout')}>
              <IconButton
                size="small"
                onClick={() => {
                  navigate('/logging-out');
                }}
                sx={{
                  color: controlIconColor,
                  width: 28,
                  height: 28,
                  '&:hover': {
                    color: controlHoverColor,
                    background: controlHoverBackground,
                  },
                }}
              >
                <LogoutRounded sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Logo area */}
        <Box
          sx={{
            mb: '12px',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {logo.type === 'img' ? (
            <img
              src={logo.src}
              alt={logo.alt}
              style={{ height: logoSize.imgHeight, objectFit: 'contain' }}
            />
          ) : (
            <SVG
              style={{ width: logoSize.svgWidth, height: logoSize.svgHeight }}
              name={logo.svgName}
            />
          )}
        </Box>

        {/* Divider line under logo */}
        <Box
          sx={{
            width: 40,
            height: '1px',
            background: dividerBackground,
            mb: '24px',
            opacity: headerVisible ? 1 : 0,
            transition: 'opacity 0.8s ease 0.2s',
          }}
        />

        {/* Tagline */}
        <Typography
          sx={{
            color: taglineColor,
            textAlign: 'center',
            maxWidth: 520,
            mb: '56px',
            lineHeight: 1.7,
            fontSize: '0.9rem',
            fontWeight: 400,
            letterSpacing: isYokohamaClient ? 0 : '0.01em',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(8px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
          }}
        >
          {CLIENT_THEME === 'yokohama'
            ? t('tagline_yokohama')
            : t('tagline_default')}
        </Typography>

        {/* Module cards — all always rendered */}
        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            width: '100%',
            maxWidth: 980,
            flexWrap: 'wrap',
          }}
        >
          {moduleCards.map((card, idx) => (
            <ModuleCard
              key={card.title}
              {...card}
              palette={palette}
              isYokohamaClient={isYokohamaClient}
              index={idx}
            />
          ))}
        </Box>

        {!isYokohamaClient && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '800px',
              height: '300px',
              borderRadius: '50%',
              background:
                'radial-gradient(ellipse, rgba(94,133,240,0.04) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};

export default HomeView;
